import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../../services/api.js';
import '../login/login.scss';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PASSWORD_RE = { len: /.{8,}/, up: /[A-Z]/, num: /\d/, sym: /[^A-Za-z0-9]/ };
const compliant = (v) => Object.values(PASSWORD_RE).every((r) => r.test(v));

function Shell({ children }) {
  return (
    <div className="container-fluid min-vh-100 d-flex align-items-center justify-content-center py-5">
      <div className="col-12 col-md-8 col-lg-5">
        <div className="card border shadow-lg login-card p-4 p-md-5">
          <div className="d-flex align-items-center mb-4">
            <i className="fa fa-bolt fa-2x me-2 text-primary" aria-hidden="true" />
            <h4 className="mb-0 fw-bold text-primary">ChargeOS</h4>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [invalid, setInvalid] = useState(false);
  const [alertMsg, setAlertMsg] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [demoLink, setDemoLink] = useState(''); // demo environments return the reset link instead of emailing it
  const [cooldown, setCooldown] = useState(0);
  const panelRef = useRef(null);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  useEffect(() => {
    if (sent) panelRef.current?.focus();
  }, [sent]);

  const send = async () => {
    setAlertMsg('');
    setSubmitting(true);
    try {
      const r = await api.forgotPassword({ email: email.trim() });
      setDemoLink(r?.demoResetLink || '');
      setSent(true);
      setCooldown(60);
    } catch (err) {
      setAlertMsg(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (!EMAIL_RE.test(email.trim())) {
      setInvalid(true);
      setAlertMsg('Please enter a valid email address.');
      return;
    }
    setInvalid(false);
    send();
  };

  return (
    <Shell>
      {sent ? (
        <div id="recovery-confirmation-panel" className="text-center" tabIndex={-1} ref={panelRef}>
          <i className="fa fa-paper-plane fa-2x mb-3 text-success" aria-hidden="true" />
          <h2 className="h5 fw-bold mb-2 text-primary">Check Your Inbox</h2>
          <p className="text-muted small mb-3" id="recovery-confirmation-msg">
            If an account exists with this email, a reset link has been sent. Please check your inbox and spam folder.
          </p>
          {demoLink && (
            <div className="alert alert-info py-2 px-3 small text-start" role="note" id="demo-reset-note">
              <i className="fa fa-flask me-1" aria-hidden="true" /><strong>Demo: no email is sent.</strong> Use this button to continue the reset.
              <a href={demoLink} className="btn btn-sm btn-primary w-100 fw-bold mt-2" id="btn-demo-reset-link">Open reset link</a>
            </div>
          )}
          <div className="alert alert-warning py-2 px-3 small text-start">
            <i className="fa fa-clock me-1" aria-hidden="true" /> For your security, the link expires in <strong>30 minutes</strong>.
          </div>
          <button type="button" id="btn-resend-link" className="btn btn-outline-secondary w-100 fw-bold mb-3" disabled={cooldown > 0 || submitting} onClick={send}>
            {cooldown > 0 ? `Resend link in ${cooldown}s` : 'Resend link'}
          </button>
          <Link to="/login" className="small fw-bold text-primary"><i className="fa fa-arrow-left me-1" aria-hidden="true" />Back to Sign In</Link>
        </div>
      ) : (
        <>
          <h1 className="h4 fw-bold mb-2 text-primary">Forgot Password?</h1>
          <p className="text-muted small">Enter the email address associated with your ChargeOS account and we will send you a secure link to reset your password.</p>
          <div id="forgot-alert" className={`alert alert-danger${alertMsg ? '' : ' d-none'}`} role="alert" aria-live="polite">
            <span id="forgot-alert-msg">{alertMsg}</span>
          </div>
          <form id="forgot-password-form" noValidate onSubmit={onSubmit}>
            <div className="mb-3">
              <label htmlFor="email" className="form-label small fw-bold">Email Address</label>
              <input
                type="email"
                id="email"
                name="email"
                className={`form-control${invalid ? ' is-invalid' : ''}`}
                placeholder="driver@example.ph"
                autoComplete="username"
                autoFocus
                required
                aria-invalid={invalid}
                aria-describedby="email-feedback"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <div id="email-feedback" className="invalid-feedback">Please enter your registered email address.</div>
            </div>
            <button type="submit" id="btn-recovery-submit" className="btn btn-primary w-100 fw-bold py-2 mb-3" disabled={submitting}>
              {submitting && <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />}
              <span id="btn-recovery-text">Send Reset Link</span>
            </button>
            <div className="text-center">
              <Link to="/login" className="small fw-bold text-primary"><i className="fa fa-arrow-left me-1" aria-hidden="true" />Back to Sign In</Link>
            </div>
          </form>
        </>
      )}
    </Shell>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [errors, setErrors] = useState({});
  const [alertMsg, setAlertMsg] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    const next = {};
    if (!compliant(password)) next.password = true;
    if (confirm !== password) next.confirm = true;
    setErrors(next);
    if (Object.keys(next).length) return;
    setSubmitting(true);
    setAlertMsg('');
    try {
      await api.resetPassword({ token, password });
      setDone(true);
    } catch (err) {
      setAlertMsg(err.message || 'Could not reset password.');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <Shell>
        <div className="text-center" id="reset-success">
          <i className="fa fa-circle-check fa-2x mb-3 text-success" aria-hidden="true" />
          <h1 className="h5 fw-bold text-primary">Password updated</h1>
          <p className="text-muted small">All existing sessions were signed out. Sign in with your new password.</p>
          <Link to="/login" className="btn btn-primary w-100 fw-bold">Go to Sign In</Link>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <h1 className="h4 fw-bold mb-2 text-primary">Set a New Password</h1>
      {!token && <div className="alert alert-danger" id="reset-alert">This reset link is invalid. <Link to="/forgot-password">Request a new one</Link>.</div>}
      {alertMsg && <div className="alert alert-danger" role="alert" id="reset-alert">{alertMsg} <Link to="/forgot-password">Request a new link</Link></div>}
      <form id="reset-password-form" noValidate onSubmit={onSubmit}>
        <div className="mb-3">
          <label htmlFor="new_password" className="form-label small fw-bold">New Password</label>
          <input id="new_password" type="password" autoComplete="new-password" required className={`form-control${errors.password ? ' is-invalid' : ''}`} value={password} onChange={(e) => setPassword(e.target.value)} />
          <div className="invalid-feedback">Min 8 characters with an uppercase letter, a number and a symbol.</div>
        </div>
        <div className="mb-3">
          <label htmlFor="confirm_new_password" className="form-label small fw-bold">Confirm Password</label>
          <input id="confirm_new_password" type="password" autoComplete="new-password" required className={`form-control${errors.confirm ? ' is-invalid' : ''}`} value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          <div className="invalid-feedback">Passwords must match.</div>
        </div>
        <button type="submit" id="btn-reset-submit" className="btn btn-primary w-100 fw-bold py-2" disabled={submitting || !token}>Reset Password</button>
      </form>
    </Shell>
  );
}
